import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  C, PageHeader, GlassCard, Badge, Skeleton, ProgressBar, Button, StatusDot, Modal,
} from "@coremonit/design-system";
import {
  Server, Cpu, MemoryStick, HardDrive, DollarSign, Activity, Boxes,
  Network, ArrowUpRight, Map, Layers, ExternalLink, Maximize2,
} from "lucide-react";
import { useCluster, useClusterK8sObjects } from "../api/hooks";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { HealthScoreRing } from "../components/HealthScoreRing";
import { RelatedResources } from "../components/RelatedResources";
import type { ClusterSummary, K8sObjectKind, K8sObjectSummary } from "@coremonit/types";

type PlateId =
  | "health" | "nodes" | "namespaces" | "pods" | "containers"
  | "deployments" | "services" | "ingresses" | "cost"
  | "cpu" | "memory" | "capacity";

interface PlateConfig {
  title: string;
  subtitle: (c: ClusterSummary, clusterId: string) => string;
  fullPagePath: (clusterId: string) => string;
  filterKind?: K8sObjectKind | K8sObjectKind[];
  customItems?: (c: ClusterSummary, objects: K8sObjectSummary[]) => Array<{ name: string; detail: string; status?: string; path?: string }>;
}

const PLATES: Record<PlateId, PlateConfig> = {
  health: {
    title: "Cluster health",
    subtitle: (c) => `Score ${c.health.score} · grade ${c.health.grade} · ${c.health.status}`,
    fullPagePath: (id) => `/health?cluster=${id}`,
    customItems: (c) => c.health.factors.map((f) => ({
      name: f.label,
      detail: f.detail ?? `Impact ${f.impact}`,
      status: f.impact < 0 ? "warning" : "healthy",
    })),
  },
  nodes: {
    title: "Nodes",
    subtitle: (c) => `${c.nodeCount} nodes in cluster`,
    fullPagePath: (id) => `/clusters/${id}/objects?kind=node`,
    filterKind: "node",
  },
  namespaces: {
    title: "Namespaces",
    subtitle: (c) => `${c.namespaceCount} namespaces`,
    fullPagePath: (id) => `/clusters/${id}/objects?kind=namespace`,
    filterKind: "namespace",
    customItems: (c, _objs) => c.namespaces.map((ns) => ({
      name: ns.name,
      detail: `${ns.pods} pods · health ${ns.health.grade}`,
      status: ns.health.status,
      path: `/clusters/${c.id}?namespace=${ns.name}`,
    })),
  },
  pods: {
    title: "Running pods",
    subtitle: (c) => `${c.runningPods} pods across all namespaces`,
    fullPagePath: (id) => `/clusters/${id}/objects?kind=pod`,
    filterKind: "pod",
  },
  containers: {
    title: "Containers",
    subtitle: (c) => `${c.runningContainers} containers including sidecars`,
    fullPagePath: (id) => `/containers?cluster=${id}`,
    filterKind: "pod",
  },
  deployments: {
    title: "Deployments",
    subtitle: (c) => `${c.deployments} active deployments`,
    fullPagePath: (id) => `/clusters/${id}/objects?kind=deployment`,
    filterKind: "deployment",
  },
  services: {
    title: "Services",
    subtitle: (c) => `${c.services} Kubernetes services`,
    fullPagePath: (id) => `/clusters/${id}/objects?kind=service`,
    filterKind: "service",
  },
  ingresses: {
    title: "Ingresses",
    subtitle: (c) => `${c.ingresses} ingress resources`,
    fullPagePath: (id) => `/clusters/${id}/objects?kind=ingress`,
    filterKind: "ingress",
  },
  cost: {
    title: "Cost estimate",
    subtitle: (c) => `$${c.costEstimateUsdMonth.toLocaleString()} / month`,
    fullPagePath: (id) => `/metrics?cluster=${id}`,
    customItems: (c) => [
      { name: "Compute", detail: `$${Math.round(c.costEstimateUsdMonth * 0.55).toLocaleString()}`, status: "healthy" },
      { name: "Storage", detail: `$${Math.round(c.costEstimateUsdMonth * 0.18).toLocaleString()}`, status: "healthy" },
      { name: "Network", detail: `$${Math.round(c.costEstimateUsdMonth * 0.12).toLocaleString()}`, status: "healthy" },
      { name: "Load balancers", detail: `$${Math.round(c.costEstimateUsdMonth * 0.09).toLocaleString()}`, status: "healthy" },
      { name: "Monitoring & logs", detail: `$${Math.round(c.costEstimateUsdMonth * 0.06).toLocaleString()}`, status: "healthy" },
    ],
  },
  cpu: {
    title: "CPU utilization",
    subtitle: (c) => `${c.usedCpuCores} / ${c.totalCpuCores} cores used`,
    fullPagePath: (id) => `/metrics?cluster=${id}&metric=cpu`,
    customItems: (c) => [
      { name: "Used", detail: `${c.usedCpuCores} cores`, status: "warning" },
      { name: "Available", detail: `${c.totalCpuCores - c.usedCpuCores} cores`, status: "healthy" },
      { name: "Utilization", detail: `${Math.round((c.usedCpuCores / c.totalCpuCores) * 100)}%`, status: "healthy" },
    ],
  },
  memory: {
    title: "Memory utilization",
    subtitle: (c) => `${c.usedMemoryGb} / ${c.totalMemoryGb} GB used`,
    fullPagePath: (id) => `/metrics?cluster=${id}&metric=memory`,
    customItems: (c) => [
      { name: "Used", detail: `${c.usedMemoryGb} GB`, status: "warning" },
      { name: "Available", detail: `${c.totalMemoryGb - c.usedMemoryGb} GB`, status: "healthy" },
      { name: "Utilization", detail: `${Math.round((c.usedMemoryGb / c.totalMemoryGb) * 100)}%`, status: "healthy" },
    ],
  },
  capacity: {
    title: "Cluster capacity",
    subtitle: (c) => `${c.availableCapacityPct}% available`,
    fullPagePath: (id) => `/clusters/${id}/objects?kind=pvc`,
    customItems: (c, _objs) => [
      { name: "Available capacity", detail: `${c.availableCapacityPct}%`, status: "healthy" },
      { name: "StatefulSets", detail: String(c.statefulSets), status: "healthy", path: `/clusters/${c.id}/objects?kind=statefulset` },
      { name: "DaemonSets", detail: String(c.daemonSets), status: "healthy", path: `/clusters/${c.id}/objects?kind=daemonset` },
      { name: "PVCs", detail: String(c.pvcs), status: "healthy", path: `/clusters/${c.id}/objects?kind=pvc` },
    ],
  },
};

export function ClusterOverviewPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nsFilter = params.get("namespace");
  const cluster = useCluster(id);
  const k8s = useClusterK8sObjects(id);
  const nav = useNavigate();
  const c = cluster.data;
  const [activePlate, setActivePlate] = useState<PlateId | null>(null);

  const plateItems = useMemo(() => {
    if (!activePlate || !c) return [];
    const config = PLATES[activePlate];
    if (config.customItems) return config.customItems(c, k8s.data?.items ?? []);

    const kinds = config.filterKind
      ? (Array.isArray(config.filterKind) ? config.filterKind : [config.filterKind])
      : [];
    let objects = k8s.data?.items ?? [];
    if (kinds.length) objects = objects.filter((o) => kinds.includes(o.kind));

    if (activePlate === "containers") {
      objects = objects.filter((o) => o.name.includes("/"));
    } else if (activePlate === "pods") {
      objects = objects.filter((o) => o.kind === "pod" && !o.name.includes("/"));
    }

    return objects.map((o) => ({
      name: o.name,
      detail: `${o.namespace} · ${o.age}`,
      status: o.status,
      path: o.path,
    }));
  }, [activePlate, c, k8s.data]);

  const openFullPage = (path: string, newTab = false) => {
    if (newTab) window.open(path, "_blank", "noopener,noreferrer");
    else nav(path);
    setActivePlate(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Breadcrumbs items={[
        { label: "Organization", path: "/" },
        { label: "Infrastructure", path: "/clusters" },
        { label: c?.name ?? id ?? "Cluster" },
      ]} />
      <PageHeader
        icon={<Server size={16} />}
        title={c?.name ?? "Cluster"}
        subtitle={c ? `${c.cloudProvider.toUpperCase()} · ${c.region} · Kubernetes ${c.kubernetesVersion}` : "Loading…"}
        badge={c && <Badge color={c.health.status === "healthy" ? "green" : c.health.status === "warning" ? "amber" : "red"}>{c.health.grade} · {c.health.score}</Badge>}
        actions={
          <>
            <Button variant="secondary" icon={<Map size={12} />} onClick={() => nav(`/clusters/${id}/topology`)}>Topology map</Button>
            <Button variant="glow" icon={<Activity size={12} />} onClick={() => nav(`/workspace?cluster=${id}`)}>Open workspace</Button>
          </>
        }
      />

      {cluster.isLoading || !c ? (
        <Skeleton height={320} radius={16} />
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 14, alignItems: "start" }}>
            <ClickablePlate onClick={() => setActivePlate("health")} ariaLabel="View health details">
              <GlassCard style={{ padding: 18, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, height: "100%" }}>
                <HealthScoreRing score={c.health.score} grade={c.health.grade} size={72} />
                <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>Health score</div>
                <div style={{ color: C.textMuted, fontSize: 10 }}>{c.health.status.toUpperCase()}</div>
              </GlassCard>
            </ClickablePlate>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
              <Stat label="Nodes" value={c.nodeCount} icon={<Server size={11} />} onClick={() => setActivePlate("nodes")} />
              <Stat label="Namespaces" value={c.namespaceCount} icon={<Layers size={11} />} onClick={() => setActivePlate("namespaces")} />
              <Stat label="Running pods" value={c.runningPods} icon={<Boxes size={11} />} onClick={() => setActivePlate("pods")} />
              <Stat label="Containers" value={c.runningContainers} icon={<Boxes size={11} />} onClick={() => setActivePlate("containers")} />
              <Stat label="Deployments" value={c.deployments} icon={<Network size={11} />} onClick={() => setActivePlate("deployments")} />
              <Stat label="Services" value={c.services} icon={<Network size={11} />} onClick={() => setActivePlate("services")} />
              <Stat label="Ingresses" value={c.ingresses} icon={<Network size={11} />} onClick={() => setActivePlate("ingresses")} />
              <Stat label="Cost / mo" value={`$${c.costEstimateUsdMonth.toLocaleString()}`} icon={<DollarSign size={11} />} color={C.amber} onClick={() => setActivePlate("cost")} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <ClickablePlate onClick={() => setActivePlate("cpu")} ariaLabel="View CPU details">
              <GlassCard style={{ padding: 16, height: "100%" }}>
                <SectionTitle icon={<Cpu size={12} />} title="CPU" />
                <div style={{ color: C.text, fontSize: 20, fontWeight: 700, marginTop: 8 }}>{c.usedCpuCores} / {c.totalCpuCores} cores</div>
                <ProgressBar value={(c.usedCpuCores / c.totalCpuCores) * 100} color={C.blue} />
              </GlassCard>
            </ClickablePlate>
            <ClickablePlate onClick={() => setActivePlate("memory")} ariaLabel="View memory details">
              <GlassCard style={{ padding: 16, height: "100%" }}>
                <SectionTitle icon={<MemoryStick size={12} />} title="Memory" />
                <div style={{ color: C.text, fontSize: 20, fontWeight: 700, marginTop: 8 }}>{c.usedMemoryGb} / {c.totalMemoryGb} GB</div>
                <ProgressBar value={(c.usedMemoryGb / c.totalMemoryGb) * 100} color={C.purple} />
              </GlassCard>
            </ClickablePlate>
            <ClickablePlate onClick={() => setActivePlate("capacity")} ariaLabel="View capacity details">
              <GlassCard style={{ padding: 16, height: "100%" }}>
                <SectionTitle icon={<HardDrive size={12} />} title="Capacity" />
                <div style={{ color: C.green, fontSize: 20, fontWeight: 700, marginTop: 8 }}>{c.availableCapacityPct}% available</div>
                <div style={{ color: C.textMuted, fontSize: 10, marginTop: 4 }}>StatefulSets {c.statefulSets} · DaemonSets {c.daemonSets} · PVCs {c.pvcs}</div>
              </GlassCard>
            </ClickablePlate>
          </div>

          <GlassCard style={{ padding: 18 }}>
            <SectionTitle icon={<Layers size={12} />} title="Namespaces" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8, marginTop: 12 }}>
              {c.namespaces.filter((n) => !nsFilter || n.name === nsFilter).map((ns) => (
                <button
                  key={ns.name}
                  onClick={() => nav(`/clusters/${id}?namespace=${ns.name}`)}
                  style={{
                    textAlign: "left", padding: 12, borderRadius: 12,
                    background: nsFilter === ns.name ? C.blueDim : C.surfaceHigh,
                    border: `1px solid ${nsFilter === ns.name ? C.borderBright : C.border}`,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{ns.name}</span>
                    <HealthScoreRing score={ns.health.score} grade={ns.health.grade} size={24} />
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 10, marginTop: 4 }}>{ns.pods} pods</div>
                </button>
              ))}
            </div>
          </GlassCard>

          <GlassCard style={{ padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <SectionTitle icon={<Boxes size={12} />} title="Kubernetes objects" />
              <Button variant="ghost" iconRight={<ArrowUpRight size={11} />} onClick={() => nav(`/clusters/${id}/objects`)}>K8s explorer</Button>
              <Button variant="ghost" iconRight={<ArrowUpRight size={11} />} onClick={() => nav(`/containers`)}>Container explorer</Button>
            </div>
            {k8s.isLoading ? <Skeleton height={120} /> : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {(k8s.data?.items ?? []).slice(0, 12).map((o) => (
                  <button
                    key={`${o.kind}_${o.name}`}
                    onClick={() => nav(o.path)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer", textAlign: "left" }}
                  >
                    <div>
                      <span style={{ color: C.textMuted, fontSize: 9, marginRight: 6 }}>{o.kind}</span>
                      <span style={{ color: C.text, fontSize: 11, fontWeight: 600 }}>{o.name}</span>
                      <span style={{ color: C.textMuted, fontSize: 10, marginLeft: 8 }}>{o.namespace}</span>
                    </div>
                    <StatusDot status={o.status} />
                  </button>
                ))}
              </div>
            )}
          </GlassCard>

          <RelatedResources links={{ clusterId: id }} />
        </>
      )}

      {activePlate && c && id && (
        <Modal
          open
          onClose={() => setActivePlate(null)}
          size="lg"
          title={PLATES[activePlate].title}
          subtitle={PLATES[activePlate].subtitle(c, id)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setActivePlate(null)}>Close</Button>
              <Button
                variant="secondary"
                icon={<ExternalLink size={12} />}
                onClick={() => openFullPage(PLATES[activePlate].fullPagePath(id), true)}
              >
                Open in new tab
              </Button>
              <Button
                variant="glow"
                icon={<Maximize2 size={12} />}
                onClick={() => openFullPage(PLATES[activePlate].fullPagePath(id))}
              >
                Open full page
              </Button>
            </>
          }
        >
          {k8s.isFetching && !plateItems.length ? (
            <Skeleton height={200} />
          ) : plateItems.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: C.textMuted, fontSize: 12 }}>
              No items found. Open the full page to explore all resources.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {plateItems.slice(0, 20).map((item, i) => (
                <button
                  key={`${item.name}_${i}`}
                  onClick={() => item.path && openFullPage(item.path)}
                  disabled={!item.path}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 12px", borderRadius: 10, textAlign: "left",
                    background: C.surfaceHigh, border: `1px solid ${C.border}`,
                    cursor: item.path ? "pointer" : "default",
                  }}
                >
                  <div>
                    <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{item.name}</div>
                    <div style={{ color: C.textMuted, fontSize: 10, marginTop: 2 }}>{item.detail}</div>
                  </div>
                  {item.status && <StatusDot status={item.status as "healthy" | "warning" | "critical"} />}
                </button>
              ))}
              {plateItems.length > 20 && (
                <div style={{ color: C.textMuted, fontSize: 10, textAlign: "center", padding: 8 }}>
                  +{plateItems.length - 20} more — open full page to see all
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function ClickablePlate({ children, onClick, ariaLabel }: { children: React.ReactNode; onClick: () => void; ariaLabel: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        background: "transparent", border: "none", padding: 0, cursor: "pointer",
        textAlign: "inherit", borderRadius: 16, transition: "transform 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
        (e.currentTarget as HTMLButtonElement).style.filter = "brightness(1.05)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = "none";
        (e.currentTarget as HTMLButtonElement).style.filter = "none";
      }}
    >
      {children}
    </button>
  );
}

function SectionTitle({ title, icon }: { title: string; icon?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
      <span style={{ color: C.blue }}>{icon}</span>{title}
    </div>
  );
}

function Stat({ label, value, icon, color, onClick }: {
  label: string; value: React.ReactNode; icon?: React.ReactNode; color?: string; onClick?: () => void;
}) {
  return (
    <ClickablePlate onClick={onClick ?? (() => {})} ariaLabel={`View ${label} details`}>
      <GlassCard style={{ padding: 12, height: "100%" }}>
        <div style={{ color: C.textMuted, fontSize: 9, fontWeight: 700, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4 }}>{icon}{label}</div>
        <div style={{ color: color ?? C.text, fontSize: 18, fontWeight: 700, marginTop: 4 }}>{value}</div>
      </GlassCard>
    </ClickablePlate>
  );
}
